import { useCallback, useEffect, useRef, useState } from 'react';

import debounce from 'lodash.debounce';

import { DEVICE_TYPE, getSearchFacadeUrl } from 'config/api';
import { DEFAULT_STRIPE_SIZE } from 'config/constants';

import { SearchFacadeResponse } from 'store/railsBuilder/reducer.types';

import NagraInstance from 'utils/axios/nagra/DefaultInstance';

import { SearchOptions, SearchType } from 'screens/Search/types';

type Params = {
    searchQuery: string;
    searchType: SearchType;
    searchOptions?: SearchOptions;
};

const EMPTY_SEARCH_RESPONSE: SearchFacadeResponse = {
    results: [],
    totalRecords: 0,
};

function useSearch({ searchQuery, searchType, searchOptions }: Params) {
    const abortController = useRef<AbortController | undefined>(undefined);

    const [searchResult, setSearchResult] = useState<SearchFacadeResponse>(
        EMPTY_SEARCH_RESPONSE,
    );
    const [loading, setLoading] = useState(false);

    const handleSearch = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-shadow
        debounce(async ({ searchQuery, searchType, searchOptions }: Params) => {
            try {
                if (abortController.current) {
                    abortController.current.abort();
                    abortController.current = undefined;
                }

                abortController.current = new AbortController();

                const { data } = await NagraInstance.get<SearchFacadeResponse>(
                    getSearchFacadeUrl({
                        q: searchQuery,
                        type: searchType,
                        limit: DEFAULT_STRIPE_SIZE,
                        device: DEVICE_TYPE,
                        ...searchOptions,
                    }),
                    { signal: abortController.current.signal },
                );

                setSearchResult(data);
            } catch (e) {
                setSearchResult(EMPTY_SEARCH_RESPONSE);
            } finally {
                setLoading(false);
            }
        }, 400),
        [],
    );

    const handleInstantSearch = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-shadow
        async ({ searchQuery, searchType, searchOptions }: Params) => {
            setLoading(true);

            try {
                if (abortController.current) {
                    abortController.current.abort();
                    abortController.current = undefined;
                }

                abortController.current = new AbortController();

                const { data } = await NagraInstance.get<SearchFacadeResponse>(
                    getSearchFacadeUrl({
                        q: searchQuery,
                        type: searchType,
                        limit: DEFAULT_STRIPE_SIZE,
                        device: DEVICE_TYPE,
                        ...searchOptions,
                    }),
                    { signal: abortController.current.signal },
                );

                setSearchResult(data);
            } catch (e) {
                setSearchResult(EMPTY_SEARCH_RESPONSE);
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    useEffect(() => {
        if (searchQuery) {
            if (searchQuery.length >= 3) {
                setLoading(true);
                handleSearch({ searchQuery, searchType, searchOptions });
            }
        } else {
            setLoading(false);
            setSearchResult(EMPTY_SEARCH_RESPONSE);
        }
    }, [searchQuery, handleSearch]);

    return { searchResult, loading, handleInstantSearch };
}

export default useSearch;
